/**
 * The Aether lockup: an inline SVG mark plus the word as HTML text.
 *
 * The mark draws the app's central computation — a level planned altitude over
 * ground that is not level, and the clearance between them — in the three
 * colours that mean the same three things everywhere else: amber is what the
 * pilot chose, ice is what Aether worked out, faint grey is the world. Geometry
 * and rationale: `docs/design/aether-ux.md` §2.2.
 *
 * The word is text and not an outline so it inherits the theme, the subsetted
 * font the app already ships and the reader's text scaling. The whole lockup is
 * one `role="img"` labelled `Aether` — never with a tagline, which belongs on
 * `/login` and nowhere else.
 */

export function Mark({ size = 24 }: { size?: number }) {
  return (
    <svg
      className="ae-mark"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      strokeLinecap="round"
      aria-hidden="true"
      focusable="false"
    >
      <path
        d="M 2 19 L 2 17.1 L 7 16.1 L 11 10.1 L 14.5 13.9 L 18 12.1 L 22 14.3 L 22 19 Z"
        fill="var(--ae-fg-faint)"
        fillOpacity="0.55"
      />
      <path d="M 2 6.1 L 22 6.1" stroke="var(--ae-accent)" strokeWidth="2" />
      <path d="M 11 7.1 L 11 9.4" stroke="var(--ae-ice)" strokeWidth="1.4" />
    </svg>
  );
}

export function Brand({
  variant = 'lockup',
  size = 'md',
}: {
  variant?: 'lockup' | 'mark';
  size?: 'sm' | 'md' | 'lg';
}) {
  const px = size === 'lg' ? 40 : size === 'sm' ? 20 : 24;
  return (
    <span className={'ae-brand ae-brand-' + size} role="img" aria-label="Aether">
      <Mark size={px} />
      {variant === 'lockup' ? (
        <span className="ae-wordmark" aria-hidden="true">
          Aether
        </span>
      ) : null}
    </span>
  );
}
